import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './showMore.css';

/* ShowMore — a capped list with a reveal (`.br-more`).

   A chosen item is never truncated away: `slice(0, limit)` alone hides a ticked
   value that sorts below the cut — reachable by deep-linking a brand whose name
   sorts late — leaving a filtered grid with nothing in the rail explaining it.
   Chosen values are hoisted to the front, then the rest, then the cut.

   The label counts: "Show all 17" tells a shopper whether it is worth the click;
   "Show more" does not. */
export default function ShowMore({ items, limit, renderItem, isChosen, moreLabel, lessLabel }) {
  const [expanded, setExpanded] = useState(false);

  const list = items || [];
  const chosen = isChosen ? list.filter(isChosen) : [];
  const rest = isChosen ? list.filter((x) => !isChosen(x)) : list;
  const ordered = chosen.concat(rest);

  /* The cut never swallows a chosen value, so it moves down to make room. */
  const cut = Math.max(limit, chosen.length);
  const shown = expanded ? ordered : ordered.slice(0, cut);
  const hasMore = ordered.length > cut;

  return (
    <>
      {shown.map(renderItem)}
      {hasMore && (
        <button
          className="br-more"
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? lessLabel : `${moreLabel} ${ordered.length}`}
        </button>
      )}
    </>
  );
}

ShowMore.propTypes = {
  items: PropTypes.array,
  limit: PropTypes.number,
  renderItem: PropTypes.func.isRequired,
  /** Marks a value as already chosen, so the cut never hides it. */
  isChosen: PropTypes.func,
  moreLabel: PropTypes.string,
  lessLabel: PropTypes.string,
};

ShowMore.defaultProps = {
  items: [],
  limit: 6,
  moreLabel: 'Show all',
  lessLabel: 'Show fewer',
};
